import { BaseModal } from "@/components/modal/base-modal";
import { Body_add_your_food_api_v1_meals_your_food_post } from "@/clients/models/Body_add_your_food_api_v1_meals_your_food_post";
import { MealTime } from "@/clients/models/MealTime";
import { TextButton } from "@/components/ui/buttons";
import { LocalStorageKeys } from "@/constants/local-storage-keys";
import { useViewportHeight } from "@/hooks/utils/use-viewport-height";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AddFromSavedFoodsModal } from "../add-from-saved-foods-modal";
import { BarcodeOption } from "./barcode-option";
import { LinkOption } from "./link-option";
import { ManualOption } from "./manual-option";
import { PhotoOption } from "./photo-option";
import { AddFoodModalMode } from "./recipe-processing-route";
import { SavedFoodsOption } from "./saved-foods-option";

interface AddFoodModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  mode?: AddFoodModalMode;
  day?: string;
  targetDate?: string;
  mealTime?: MealTime;
}

export const AddFoodModal = memo(function AddFoodModal({
  isOpen,
  onDismiss,
  mode = "default",
  day,
  targetDate,
  mealTime,
}: AddFoodModalProps) {
  const viewportHeight = useViewportHeight();
  const pageRef = useRef<HTMLElement>(null);
  const [isSavedFoodsModalOpen, setIsSavedFoodsModalOpen] = useState(false);
  const apiMealTime = useMemo<
    Body_add_your_food_api_v1_meals_your_food_post.meal_time | undefined
  >(() => {
    if (!mealTime) {
      return undefined;
    }
    return mealTime as unknown as Body_add_your_food_api_v1_meals_your_food_post.meal_time;
  }, [mealTime]);
  const initialBreakpoint = useMemo(() => {
    if (mode === "extra-food" || mode === "add-to-meal-time") {
      return 430 / viewportHeight;
    }
    return 389 / viewportHeight;
  }, [mode, viewportHeight]);

  // Called when any option completes its flow
  const handleComplete = useCallback(() => {
    onDismiss();
  }, [onDismiss]);

  const handleOpenSavedFoodsModal = useCallback(() => {
    setIsSavedFoodsModalOpen(true);
  }, []);

  const handleCloseSavedFoodsModal = useCallback(() => {
    setIsSavedFoodsModalOpen(false);
  }, []);

  const handleSavedFoodsComplete = useCallback(() => {
    setIsSavedFoodsModalOpen(false);
    onDismiss();
  }, [onDismiss]);

  const handleBaseModalDismiss = useCallback(() => {
    // Ignore dismiss fired while transitioning from option sheet to full-screen modal.
    if (isSavedFoodsModalOpen) {
      return;
    }
    onDismiss();
  }, [isSavedFoodsModalOpen, onDismiss]);

  useEffect(() => {
    if (!isOpen) {
      setIsSavedFoodsModalOpen(false);
      return;
    }

    if (mode !== "extra-food") {
      return;
    }

    const shouldReopenSavedFoods =
      localStorage.getItem(LocalStorageKeys.EXTRA_FOOD_REOPEN_SAVED_FOODS_MODAL) === "true";

    if (shouldReopenSavedFoods) {
      localStorage.removeItem(LocalStorageKeys.EXTRA_FOOD_REOPEN_SAVED_FOODS_MODAL);
      setIsSavedFoodsModalOpen(true);
    }
  }, [isOpen, mode]);

  return (
    <>
      <div ref={pageRef as React.RefObject<HTMLDivElement>} />
      <BaseModal
        isOpen={isOpen && !isSavedFoodsModalOpen}
        onDismiss={handleBaseModalDismiss}
        backdropDismiss={true}
        initialBreakpoint={initialBreakpoint}
        breakpoints={[0, initialBreakpoint, 0.9]}
      >
        <div className="safe-area-bottom flex flex-col gap-6 px-8 pt-8 pb-8">
          <h2 className={cn(textVariants.title2, "h-8.5 text-center")}>Add your own food</h2>

          <div className="flex flex-col gap-2">
            {(mode === "extra-food" || mode === "add-to-meal-time") && (
              <SavedFoodsOption
                onClick={handleOpenSavedFoodsModal}
                disabled={isSavedFoodsModalOpen}
              />
            )}
            {/* <SearchOption pageRef={pageRef} disabled={true} onComplete={handleComplete} /> */}
            <LinkOption
              pageRef={pageRef}
              onComplete={handleComplete}
              mode={mode}
              day={day}
              targetDate={targetDate}
              mealTime={apiMealTime}
            />
            <PhotoOption
              onComplete={handleComplete}
              mode={mode}
              day={day}
              targetDate={targetDate}
              mealTime={apiMealTime}
            />
            <ManualOption
              pageRef={pageRef}
              onComplete={handleComplete}
              mode={mode}
              day={day}
              targetDate={targetDate}
              mealTime={apiMealTime}
            />
            <BarcodeOption disabled={true} />
            <TextButton color="black" onClick={onDismiss}>
              Cancel
            </TextButton>
          </div>
        </div>
      </BaseModal>

      {(mode === "extra-food" || mode === "add-to-meal-time") && (
        <AddFromSavedFoodsModal
          isOpen={isSavedFoodsModalOpen}
          onDismiss={handleCloseSavedFoodsModal}
          onComplete={handleSavedFoodsComplete}
          day={day}
          targetDate={targetDate}
          mode={mode}
          mealTime={mealTime}
        />
      )}
    </>
  );
});
